import { AuthChangeEvent, Session } from '@supabase/supabase-js';

import { AuthProvider } from './types';

import supabaseProviderService from '@/features/auth/services/supabase-provider.service';
import { AuthProviderType } from '@/features/auth/types/enums/auth-provider-type';
import { AuthSession } from '@/features/auth/types/interfaces/auth-session';

const mapSupabaseSession = (session: Session | null): AuthSession | null => {
  if (!session?.access_token || !session.user) {
    return null;
  }

  return {
    access_token: session.access_token,
    provider: AuthProviderType.SUPABASE,
    user: {
      id: session.user.id,
      email: session.user.email ?? null,
      name: session.user.user_metadata?.name ?? session.user.email ?? null,
    },
  };
};

class SupabaseAuthProvider implements AuthProvider {
  readonly type = AuthProviderType.SUPABASE;

  private getClientOrThrow = () => {
    supabaseProviderService.init();

    const client = supabaseProviderService.client;

    if (!client) {
      throw new Error('Supabase client not found');
    }

    return client;
  };

  loginWithSupabase = async (email: string, password: string) => {
    const response = await this.getClientOrThrow().auth.signInWithPassword({ email, password });

    if (response.error) {
      throw response.error;
    }
  };

  loginWithSupabaseSSO = async (providerId: string, redirectTo: string) => {
    const response = await this.getClientOrThrow().auth.signInWithSSO({
      providerId,
      options: {
        redirectTo,
      },
    });

    if (response.error) {
      throw response.error;
    }

    return response.data?.url ?? null;
  };

  getSession = async () => {
    const {
      data: { session },
      error,
    } = await this.getClientOrThrow().auth.getSession();

    if (error) {
      return null;
    }

    return mapSupabaseSession(session);
  };

  getAccessToken = async () => {
    const session = await this.getSession();

    return session?.access_token ?? null;
  };

  logout = async () => {
    const response = await this.getClientOrThrow().auth.signOut();

    if (response.error) {
      throw response.error;
    }
  };

  onAuthStateChange = (
    callback: (event: AuthChangeEvent, session: AuthSession | null) => void,
  ): (() => void) => {
    const {
      data: { subscription },
    } = this.getClientOrThrow().auth.onAuthStateChange((event, session) => {
      callback(event, mapSupabaseSession(session));
    });

    return () => {
      subscription.unsubscribe();
    };
  };
}

export const supabaseAuthProvider = new SupabaseAuthProvider();
